import { formatChipsShort } from '@polf/shared/locale'

/**
 * One row in a standings list — used by the compact StandingsBoard on
 * Bordet, and by the full-page Leaderboard route. Two variants:
 *  - `active`: shows live currentStack, ranked by rank prop
 *  - `eliminated`: shows finishing position, no stack
 *
 * Auth player's row gets the "you" highlight per design.md §8.1 — green-50
 * background + 3px green-700 left border + "Du" pill.
 */
type Variant =
  | { kind: 'active'; rank: number; currentStack: number }
  | { kind: 'eliminated'; finishingPosition: number }

const StandingsRow = ({
  playerName,
  isYou,
  variant,
}: {
  playerName: string
  isYou: boolean
  variant: Variant
}) => (
  <li
    data-you={isYou ? 'true' : undefined}
    className={`relative flex items-center gap-3 px-4 py-2 border-b border-ink-100 last:border-b-0 ${
      isYou ? 'bg-green-50' : ''
    }`}
  >
    {isYou ? (
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-green-700"
      />
    ) : null}
    <span
      className={`font-mono num text-ink-500 text-right font-semibold ${
        variant.kind === 'eliminated' ? 'w-8' : 'w-6'
      }`}
    >
      {variant.kind === 'active' ? variant.rank : variant.finishingPosition}.
    </span>
    <span
      className={`flex-1 text-sm truncate ${
        // eslint-disable-next-line no-nested-ternary
        isYou
          ? 'font-bold text-green-800'
          : variant.kind === 'eliminated'
          ? 'font-medium text-ink-700'
          : 'font-semibold text-ink-900'
      }`}
    >
      {playerName}
      {isYou ? (
        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full align-middle">
          Du
        </span>
      ) : null}
    </span>
    {variant.kind === 'active' ? (
      <span className="font-mono num font-bold text-sm text-ink-900">
        {formatChipsShort(variant.currentStack)}
      </span>
    ) : null}
  </li>
)

export default StandingsRow
